import { PHASE, RATINGS } from "../../../common/constants.ts";
import actions from "./actions.ts";
import processInputs from "./processInputs.ts";
import {
  contractNegotiation,
  draft,
  league,
  player,
  team,
  trade,
  expansionDraft,
  realRosters,
  freeAgents,
} from "../core/index.ts";
import { idb } from "../db/index.ts";
import {
  checkNaNs,
  env,
  g,
  helpers,
  local,
  lock,
  updatePlayMenu,
  toUI,
  recomputeLocalUITeamOvrs,
  updatePhase,
  logEvent,
  getNewLeagueLid,
} from "../util/index.ts";
import * as views from "../views/index.ts";
import {
  type Conditions,
  type Env,
  type Local,
  type LiveGameStatusUI,
  type LockName,
  type Player,
  type UpdateEvents,
  type TradeTeams,
  type Options,
  type GetLeagueOptions,
  type Conf,
  type Div,
  type League,
  type LeagueCreationType,
} from "../../../common/types.ts";
import { getScore } from "../core/player/checkJerseyNumberRetirement.ts";
import type { NewLeagueTeam } from "../../../common/types/newLeague.ts";
import {
  normalizeNewLeagueSeedInfo,
  type NewLeagueSeedInfo,
} from "../../../common/types/leagueSeed.ts";
import {
  getActualAttendance,
  getAutoTicketPriceByTid,
  getBaseAttendance,
} from "../core/game/attendance.ts";
import createStreamFromLeagueObject from "../core/league/create/createStreamFromLeagueObject.ts";
import playMenu from "./playMenu.ts";
import { maxBy } from "../../../common/utils.ts";
import { getPlayer } from "../views/player.ts";
import { getAchievementsSheet } from "../views/achievementsShared.ts";
import { getDraftPicksSheet } from "../views/draftPicksShared.ts";
import { getInboxSheet, getMessageSheet } from "../views/inboxShared.ts";
import { getInjuriesSheet } from "../views/injuriesShared.ts";
import { getTeamHistorySheet } from "../views/teamHistoryShared.ts";
import { getTeamRosterSheet } from "../views/roster.ts";
import {
  getTradeQuote,
  suggestTradePackage,
  previewTradeOffer,
  evaluateTradeOffer,
  confirmTradeOffer as confirmTradeOfferCore,
} from "../core/trade/workbench.ts";
import { beforeLeague, beforeNonLeague } from "../util/beforeView.ts";
import { actualPhase } from "../util/actualPhase.ts";
import type { NewLeagueSettings } from "../../../common/types/newLeague.ts";
import { getNumPlayersTradedAwayNormalizedAll } from "../core/player/getNumPlayersTradedAwayNormalized.ts";
import { getAdjustedTicketPrice } from "../../../common/getAdjustedTicketPrice.ts";
import { generateContractOptions } from "../core/contractNegotiation/generateContractOptions.ts";

const hasOwn = <T extends object>(object: T, key: PropertyKey): key is keyof T =>
  Object.hasOwn(object, key);

type ProcessInput = (params: any, ctxBBGM: any) => Record<string, any> | undefined;

type View = (
  inputs: Record<string, any>,
  updateEvents: UpdateEvents,
  prevData: any,
  conditions: Conditions,
) => Promise<void | Record<string, any>>;

const acceptContractNegotiation = async ({
  pid,
  amount,
  exp,
}: {
  pid: number;
  amount: number;
  exp: number;
}) => {
  const negotiation = await contractNegotiation.get(pid);
  if (typeof negotiation === "string") {
    return negotiation;
  }

  const result = await contractNegotiation.accept({ negotiation, amount, exp });

  if (result === undefined) {
    // Only do this if there was no error, and don't await because it makes the UI slow
    void contractNegotiation.afterAccept(negotiation.tid);
  }

  return result;
};

const beforeView = async (
  {
    inLeague,
    lidCurrent,
    lidUrl,
  }: {
    inLeague: boolean;
    lidCurrent: number | undefined;
    lidUrl: number | undefined;
  },
  conditions: Conditions,
) => {
  if (inLeague) {
    // idb.league check is for Safari weirdness - seems we need to reinitialize state sometimes because it is lost? idk
    if (lidUrl !== undefined && (lidUrl !== lidCurrent || idb.league === undefined)) {
      await beforeLeague(lidUrl, conditions);
    }
  } else if (lidCurrent !== undefined) {
    await beforeNonLeague(conditions);
  }
};

const cancelContractNegotiation = async (pid: number) => {
  const result = await contractNegotiation.cancel(pid);
  await toUI("realtimeUpdate", [["playerMovement"]]);
  return result;
};

const getUpdateWatch = (players: Player[]) => {
  const updateWatch: Record<number, number> = {};
  for (const p of players) {
    updateWatch[p.pid] = p.watch ?? 0;
  }
  return updateWatch;
};

const createLeague = async (
  {
    name,
    tid,
    shuffleRosters,
    getLeagueOptions,
    keptKeys,
    confs,
    divs,
    teamsFromInput,
    settings,
    sourceInfo: sourceInfoInput,
    startingSeasonFromInput,
    leagueCreationID,
  }: {
    name: string;
    tid: number;
    shuffleRosters: boolean;
    getLeagueOptions: GetLeagueOptions | undefined;
    keptKeys: string[];
    confs: Conf[];
    divs: Div[];
    teamsFromInput: NewLeagueTeam[];
    settings: NewLeagueSettings;
    sourceInfo: NewLeagueSeedInfo | undefined;
    startingSeasonFromInput: string | undefined;
    leagueCreationID: number;
  },
  conditions: Conditions,
): Promise<number> => {
  const sourceInfo = normalizeNewLeagueSeedInfo(sourceInfoInput);
  const creationType: LeagueCreationType = getLeagueOptions?.type === "real" ? "real" : "random";

  const keys = new Set([...keptKeys, "startingSeason", "version"]);

  const setLeagueCreationStatus = (status: string) => {
    toUI(
      "updateLocal",
      [
        {
          leagueCreation: {
            id: leagueCreationID,
            status,
          },
        },
      ],
      conditions,
    );
  };

  setLeagueCreationStatus("Initializing...");

  let actualTid = tid;
  let stream: ReadableStream | undefined;
  if (getLeagueOptions) {
    const realLeague = await realRosters.getLeague(getLeagueOptions);

    if (getLeagueOptions.type === "real") {
      if (getLeagueOptions.realStats === "all") {
        keys.add("awards");
        keys.add("playoffSeries");
      }

      if (getLeagueOptions.phase >= PHASE.PLAYOFFS) {
        keys.add("awards");
        keys.add("draftLotteryResults");
        keys.add("draftPicks");
        keys.add("playoffSeries");
      }
    }

    // Since inactive teams are included if realStats=="all", need to translate tid and overwrite sourceInfo.teams
    if (getLeagueOptions.type === "real" && getLeagueOptions.realStats === "all") {
      const srID = sourceInfo.teams?.[tid]?.srID;
      actualTid = realLeague.teams.findIndex((t) => t.srID === srID);
      if (!srID || actualTid < 0) {
        throw new Error("Error finding tid");
      }
    }

    // Definitley need this for realStats=="all", but maybe elsewhere too. This is needed because we don't know if we're keeping history or not when we call getLeagueInfo to display the team/settings in the UI.
    sourceInfo.gameAttributes = realLeague.gameAttributes;
    sourceInfo.startingSeason = realLeague.startingSeason;
    sourceInfo.teams = realLeague.teams;

    stream = createStreamFromLeagueObject(realLeague);
  } else {
    stream = createStreamFromLeagueObject({});
  }

  if (!stream) {
    throw new Error("No stream");
  }

  const lid = await getNewLeagueLid();

  await league.createStream(stream, {
    conditions,
    creationType,
    confs,
    divs,
    sourceInfo,
    getLeagueOptions,
    lid,
    keptKeys: keys,
    name,
    setLeagueCreationStatus,
    settings,
    shuffleRosters,
    startingSeasonFromInput,
    teamsFromInput,
    tid: actualTid,
  });

  delete (globalThis as any).stream0;

  if (settings.giveMeWorstRoster) {
    await league.swapWorstRoster(false);
  }

  toUI(
    "updateLocal",
    [
      {
        leagueCreation: undefined,
      },
    ],
    conditions,
  );

  return lid;
};

const draftLottery = async () => {
  const { draftLotteryResult } = await draft.genOrder();
  return draftLotteryResult;
};

const draftUser = async (pid: number, conditions: Conditions) => {
  if (lock.get("drafting")) {
    return;
  }

  const draftPicks = await draft.getOrder();
  const dp = draftPicks[0];

  if (dp && g.get("userTids").includes(dp.tid)) {
    draftPicks.shift();
    await draft.selectPlayer(dp, pid);
    await draft.afterPicks(draftPicks.length === 0, conditions);
  }
};

const getBornLoc = async (pid: number) => {
  const p = await idb.getCopy.players({ pid });
  if (p) {
    return p.born.loc;
  }
};

const getLeagueInfo = async (options: Parameters<typeof realRosters.getLeagueInfo>[0]) => {
  return realRosters.getLeagueInfo(options);
};

const getLeagues = async () => {
  return idb.meta.getAll("leagues");
};

const getNegotiationProps = async (pid: number) => {
  const userTid = g.get("userTid");

  const negotiation = await contractNegotiation.get(pid);
  if (typeof negotiation === "string") {
    return negotiation;
  }

  const p2 = await idb.cache.players.get(negotiation.pid);
  let p;
  if (p2) {
    p = await idb.getCopy.playersPlus(p2, {
      attrs: ["pid", "name", "age", "contract", "face", "imgURL", "watch"],
      ratings: ["ovr", "pot"],
      season: g.get("season"),
      showNoStats: true,
      showRookies: true,
      fuzz: true,
    });
  }

  // This can happen if a negotiation is somehow started with a retired player, or a player was deleted
  if (!p || !p2) {
    await contractNegotiation.cancel(negotiation.pid);
    return "Invalid negotiation. Please try again.";
  }

  p.mood = await player.moodInfos(p2);

  const contractOptions = await generateContractOptions(
    negotiation,
    {
      amount: p.mood.user.contractAmount / 1000,
      exp: p.contract.exp,
    },
    p.ratings.ovr,
  );
  if (contractOptions.length === 0 && g.get("phase") === PHASE.RESIGN_PLAYERS) {
    const t = await idb.cache.teams.get(userTid);
    if (
      t &&
      t.firstSeasonAfterExpansion !== undefined &&
      t.firstSeasonAfterExpansion - 1 === g.get("season")
    ) {
      contractOptions.push({
        exp: g.get("season") + 1,
        years: 1,
        amount: p.mood.user.contractAmount / 1000,
        smallestAmount: true,
      });
    }
  }

  const payroll = await team.getPayroll(userTid);

  const t = await idb.getCopy.teamsPlus({
    tid: g.get("userTid"),
    attrs: ["colors", "jersey"],
  });
  if (!t) {
    throw new Error("Should never happen");
  }

  return {
    capSpace: (g.get("salaryCap") - payroll) / 1000,
    challengeNoRatings: g.get("challengeNoRatings"),
    contractOptions,
    salaryCapType: g.get("salaryCapType"),
    payroll: payroll / 1000,
    p,
    resigning: negotiation.resigning,
    salaryCap: g.get("salaryCap") / 1000,
    t,
  };
};

const getLocal = async (name: keyof Local) => {
  return local[name];
};

const getPlayerRangeFooterStats = async ({
  pid,
  seasonRange,
}: {
  pid: number;
  seasonRange: [number, number];
}) => {
  const pRaw = await idb.getCopy.players(
    {
      pid,
    },
    "noCopyCache",
  );
  if (!pRaw) {
    return;
  }

  const p = await getPlayer(pRaw, seasonRange);

  if (p) {
    // Would be nice to only return the one we need, but returning them all means tab changes are free
    return {
      careerStatsCombined: p.careerStatsCombined,
      careerStatsPlayoffs: p.careerStatsPlayoffs,
      careerStats: p.careerStats,
    };
  }
};

const getPlayerWatch = async (pid: number) => {
  if (Number.isNaN(pid)) {
    return 0;
  }

  const p = await idb.cache.players.get(pid);

  if (p) {
    return p.watch ?? 0;
  }
  const p2 = await idb.getCopy.players({ pid }, "noCopyCache");
  if (p2) {
    return p2.watch ?? 0;
  }

  return 0;
};

const getProjectedAttendance = async ({
  ticketPrice,
  tid,
}: {
  ticketPrice: number;
  tid: number;
}) => {
  if (Number.isNaN(ticketPrice)) {
    return 0;
  }

  const teamSeasons = await idb.cache.teamSeasons.indexGetAll("teamSeasonsByTidSeason", [
    [tid, g.get("season") - 2],
    [tid, g.get("season")],
  ]);
  const teamSeason = teamSeasons.at(-1);
  if (!teamSeason) {
    return 0;
  }

  const baseAttendance = getBaseAttendance({
    hype: teamSeason.hype,
    pop: teamSeason.pop,
    playoffs: false,
  });
  const adjustedTicketPrice = getAdjustedTicketPrice(ticketPrice, false);
  const attendance = await getActualAttendance({
    baseAttendance,
    randomize: false,
    stadiumCapacity: teamSeason.stadiumCapacity,
    teamSeasons,
    tid: teamSeason.tid,
    adjustedTicketPrice,
  });

  return attendance;
};

const ping = async () => {
  return;
};

let initRan = false;
const init = async (inputEnv: Env, conditions: Conditions) => {
  Object.assign(env, inputEnv);

  // Kind of hacky, only run this for the first host tab
  if (!initRan) {
    initRan = true;
    checkNaNs();
  }

  // Send options to all new tabs
  const attributesStore = (await idb.meta.transaction("attributes")).store;
  const options = ((await attributesStore.get("options")) ?? {}) as Options;
  await toUI("updateLocal", [{ fullNames: options.fullNames, units: options.units }], conditions);
};

const lockSet = async ([name, value]: [LockName, boolean]) => {
  await lock.set(name, value);
};

const ratingsStatsPopoverInfo = async ({ pid, season }: { pid: number; season?: number }) => {
  const blankObj = {
    name: undefined,
    ratings: undefined,
    stats: undefined,
  };

  if (Number.isNaN(pid) || typeof pid !== "number") {
    return blankObj;
  }

  let p;
  if (local.liveSimRatingsStatsPopoverPlayers) {
    p = local.liveSimRatingsStatsPopoverPlayers[pid];
  }

  if (!p) {
    p = await idb.getCopy.players(
      {
        pid,
      },
      "noCopyCache",
    );
  }

  if (!p) {
    return blankObj;
  }

  const currentSeason = g.get("season");

  let actualSeason: number | undefined;
  let draftProspect = false;
  if (season !== undefined) {
    // For draft prospects, show their draft season, otherwise they will be skipped due to not having ratings in g.get("season")
    actualSeason = p.draft.year > season ? p.draft.year : season;
  } else {
    actualSeason = p.draft.year > currentSeason ? p.draft.year : currentSeason;
  }

  // If player has no stats that season and is not a draft prospect, show career stats
  if (p.draft.year < actualSeason && !p.ratings.some((row) => row.season === actualSeason)) {
    actualSeason = undefined;
  }

  if (p.draft.year === actualSeason) {
    draftProspect = true;
    actualSeason = undefined;
  }

  const stats = [
    "pts",
    "trb",
    "ast",
    "blk",
    "stl",
    "tov",
    "min",
    "per",
    "ewa",
    "tsp",
    "tpar",
    "ftr",
    "fgp",
    "tpp",
    "ftp",
  ];

  const attrs = ["name", "jerseyNumber", "tid", "age", "abbrev"];
  const ratings = ["pos", "ovr", "pot", "season", "tid", ...RATINGS, "abbrev"];

  const p2 = await idb.getCopy.playersPlus(p, {
    attrs,
    ratings,
    stats: ["tid", "season", "playoffs", ...stats],
    season: actualSeason,
    showNoStats: true,
    showRetired: true,
    oldStats: true,
    fuzz: true,
  });
  if (actualSeason === undefined) {
    if (draftProspect) {
      p2.ratings = p2.ratings[0];
    } else {
      // Peak ratings
      p2.ratings = maxBy(p.ratings, "ovr");
    }
    p2.age = p2.ratings.season - p.born.year;

    p2.stats = p2.careerStats;
    delete p2.careerStats;
  }
  if (actualSeason === undefined || actualSeason < currentSeason) {
    p2.abbrev = p2.ratings.abbrev;
    p2.tid = p2.ratings.tid;
  }
  delete p2.ratings.abbrev;
  delete p2.ratings.tid;
  delete p2.stats.playoffs;
  delete p2.stats.season;
  delete p2.stats.tid;

  let type: "career" | "current" | "draft" | number;
  if (draftProspect) {
    type = "draft";
  } else if (actualSeason === undefined) {
    type = "career";
  } else if (actualSeason >= currentSeason) {
    type = "current";
  } else {
    type = actualSeason;
  }
  return {
    ...p2,
    type,
  };
};

const releasePlayer = async ({ pids }: { pids: number[] }) => {
  if (pids.length === 0) {
    return;
  }

  const players = await idb.getCopies.players({ pids });
  if (players.length !== pids.length) {
    return "Player not found";
  }

  if (players.some((p) => p.tid !== g.get("userTid"))) {
    return "You aren't allowed to do this";
  }

  for (const p of players) {
    const justDrafted = helpers.justDrafted(p, g.get("phase"), g.get("season"));

    await player.release(p, justDrafted);
  }

  await toUI("realtimeUpdate", [["playerMovement"]]);
  await recomputeLocalUITeamOvrs();

  // Purposely after realtimeUpdate, so the UI update happens without waiting for this to complete
  await freeAgents.normalizeContractDemands({
    type: "dummyExpiringContracts",
    pids,
  });
};

const removeLastTeam = async () => {
  const tid = g.get("numTeams") - 1;
  const players = await idb.cache.players.indexGetAll("playersByTid", tid);

  const numPlayersTradedAwayNormalized = await getNumPlayersTradedAwayNormalizedAll();
  for (const p of players) {
    player.addToFreeAgents(p, numPlayersTradedAwayNormalized);
    await idb.cache.players.put(p);
  }

  // Delete draft picks, and return traded ones to original owner
  await draft.genPicks();

  const teamSeasons = await idb.cache.teamSeasons.indexGetAll("teamSeasonsByTidSeason", [
    [tid],
    [tid, "Z"],
  ]);

  for (const teamSeason of teamSeasons) {
    await idb.cache.teamSeasons.delete(teamSeason.rid);
  }

  const teamStats = [
    ...(await idb.cache.teamStats.indexGetAll("teamStatsByPlayoffsTid", [
      [false, tid],
      [false, tid],
    ])),
    ...(await idb.cache.teamStats.indexGetAll("teamStatsByPlayoffsTid", [
      [true, tid],
      [true, tid],
    ])),
  ];

  for (const teamStat of teamStats) {
    await idb.cache.teamStats.delete(teamStat.rid);
  }

  await idb.cache.teams.delete(tid);
  const updatedGameAttributes: any = {
    numActiveTeams: g.get("numActiveTeams") - 1,
    numTeams: g.get("numTeams") - 1,
    teamInfoCache: g.get("teamInfoCache").slice(0, -1),
    userTids: g.get("userTids").filter((userTid) => userTid !== tid),
  };

  if (g.get("userTid") === tid && tid > 0) {
    updatedGameAttributes.userTid = tid - 1;

    if (!updatedGameAttributes.userTids.includes(tid - 1)) {
      updatedGameAttributes.userTids.push(tid - 1);
    }
  }

  await league.setGameAttributes(updatedGameAttributes);

  // Manually removing a new team can mess with scheduled events, because they are indexed on tid. Let's try to adjust them.
  // Delete future scheduledEvents for the deleted team, and decrement future tids for new teams
  const scheduledEvents = await idb.getCopies.scheduledEvents(undefined, "noCopyCache");
  for (const scheduledEvent of scheduledEvents) {
    if (scheduledEvent.season < g.get("season")) {
      await idb.cache.scheduledEvents.delete(scheduledEvent.id);
    } else if (scheduledEvent.type === "expansionDraft") {
      let updated;
      let hasTid;
      for (const t2 of scheduledEvent.info.teams) {
        if (typeof t2.tid === "number" && tid < t2.tid) {
          t2.tid -= 1;
          updated = true;
        } else if (typeof t2.tid === "number" && tid === t2.tid) {
          hasTid = true;
        }
      }

      if (hasTid) {
        scheduledEvent.info.teams = scheduledEvent.info.teams.filter((t2) => t2.tid !== tid);
        updated = true;
      }

      if (updated) {
        await idb.cache.scheduledEvents.put(scheduledEvent);
      }
    } else if (scheduledEvent.type == "contraction" || scheduledEvent.type === "teamInfo") {
      if (tid === scheduledEvent.info.tid) {
        await idb.cache.scheduledEvents.delete(scheduledEvent.id);
      } else if (tid < scheduledEvent.info.tid) {
        scheduledEvent.info.tid -= 1;
        await idb.cache.scheduledEvents.put(scheduledEvent);
      }
    }
  }

  await idb.cache.flush();
};

const removeLeague = async (lid: number) => {
  await league.remove(lid);
  await toUI("realtimeUpdate", [["leagues"]]);
};

const reorderRoster = async (sortedPids: number[]) => {
  for (const [rosterOrder, pid] of sortedPids.entries()) {
    const p = await idb.cache.players.get(pid);
    if (!p) {
      throw new Error("Invalid pid");
    }

    if (p.rosterOrder !== rosterOrder) {
      p.rosterOrder = rosterOrder;
      await idb.cache.players.put(p);
    }
  }

  const t = await idb.cache.teams.get(g.get("userTid"));
  if (t) {
    t.keepRosterSorted = false;
    await idb.cache.teams.put(t);
  }

  await toUI("realtimeUpdate", [["gameAttributes", "playerMovement"]]);
};

const retiredJerseyNumberDelete = async ({ tid, i }: { tid: number; i: number }) => {
  const t = await idb.cache.teams.get(tid);
  if (!t) {
    throw new Error("Invalid tid");
  }

  if (t.retiredJerseyNumbers) {
    t.retiredJerseyNumbers = t.retiredJerseyNumbers.filter((row, j) => i !== j);
    await idb.cache.teams.put(t);
    await toUI("realtimeUpdate", [["retiredJerseys", "playerMovement"]]);
  }
};

const retiredJerseyNumberUpsert = async ({
  tid,
  i,
  info,
}: {
  tid: number;
  i?: number;
  info: {
    number: string;
    seasonRetired: number;
    seasonTeamInfo: number;
    pid: number | undefined;
    text: string;
  };
}) => {
  const t = await idb.cache.teams.get(tid);
  if (!t) {
    throw new Error("Invalid tid");
  }

  if (Number.isNaN(info.seasonRetired)) {
    throw new Error("Invalid value for seasonRetired");
  }
  if (Number.isNaN(info.seasonTeamInfo)) {
    throw new Error("Invalid value for seasonTeamInfo");
  }
  if (Number.isNaN(info.pid)) {
    throw new Error("Invalid value for player ID number");
  }

  let playerText = "";
  let score: number | undefined;
  if (info.pid !== undefined) {
    const p = await idb.getCopy.players({ pid: info.pid }, "noCopyCache");
    if (p) {
      playerText = `<a href="${helpers.leagueUrl(["player", p.pid])}">${
        p.firstName
      } ${p.lastName}</a>'s `;

      score = getScore(p, tid);
    }
  }

  // Insert or update?
  let saveEvent = false;
  if (i === undefined) {
    saveEvent = true;

    if (!t.retiredJerseyNumbers) {
      t.retiredJerseyNumbers = [];
    }

    t.retiredJerseyNumbers.push({
      ...info,
      score,
    });
  } else {
    if (!t.retiredJerseyNumbers) {
      throw new Error("Cannot edit when retiredJerseyNumbers is undefined");
    }

    if (i >= t.retiredJerseyNumbers.length) {
      throw new Error("Invalid index");
    }

    const prevNumber = t.retiredJerseyNumbers[i]?.number;
    if (prevNumber !== info.number) {
      saveEvent = true;
    }

    t.retiredJerseyNumbers[i] = {
      ...info,
      score,
    };
  }

  if (saveEvent) {
    logEvent({
      type: "retiredJersey",
      text: `The ${t.region} ${t.name} retired ${playerText}#${info.number}.`,
      showNotification: false,
      pids: info.pid ? [info.pid] : [],
      tids: [t.tid],
      score: 20,
    });
  }

  await idb.cache.teams.put(t);

  // Handle players who have the retired jersey number
  if (actualPhase() <= PHASE.PLAYOFFS) {
    const players = await idb.cache.players.indexGetAll("playersByTid", tid);
    for (const p of players) {
      if (p.stats.length === 0) {
        continue;
      }

      const jerseyNumber = helpers.getJerseyNumber(p);
      if (jerseyNumber === info.number) {
        player.setJerseyNumber(p, await player.genJerseyNumber(p));
        await idb.cache.players.put(p);
      }
    }
  }

  await toUI("realtimeUpdate", [["retiredJerseys", "playerMovement"]]);
};

const runBefore = async (
  {
    viewId,
    params,
    ctxBBGM,
    updateEvents,
    prevData,
  }: {
    viewId: string;
    params: any;
    ctxBBGM: any;
    updateEvents: UpdateEvents;
    prevData: any;
  },
  conditions: Conditions,
): Promise<void | {
  [key: string]: any;
}> => {
  // Special case for errors, so that the condition right below (when league is loading) does not cause no update
  if (viewId === "error") {
    return {};
  }

  if (typeof g.get("lid") === "number" && !local.leagueLoaded) {
    return;
  }

  let inputs: Record<string, any> | undefined;
  if (hasOwn(processInputs, viewId)) {
    const processInput = processInputs[viewId] as ProcessInput;
    inputs = processInput(params, ctxBBGM);
  }
  if (inputs === undefined) {
    // Return empty object rather than undefined
    inputs = {};
  }

  if (typeof inputs.redirectUrl === "string") {
    // Short circuit from processInputs alone
    return {
      redirectUrl: inputs.redirectUrl,
    };
  }

  const view = hasOwn(views, viewId) ? (views[viewId] as View) : undefined;

  if (view) {
    const data = await view(inputs, updateEvents, prevData, conditions);
    return data ?? {};
  }

  return {};
};

const reSignAll = async (players: any[]) => {
  const userTid = g.get("userTid");
  let negotiations = await idb.cache.negotiations.getAll();

  // For Multi Team Mode, might have other team's negotiations going on
  negotiations = negotiations.filter((negotiation) => negotiation.tid === userTid);

  if (negotiations.length > 0) {
    for (const negotiation of negotiations) {
      const p = players.find((p) => p.pid === negotiation.pid);

      if (p && p.mood.user.willing) {
        const errorMsg = await contractNegotiation.accept({
          negotiation,
          amount: p.mood.user.contractAmount,
          exp: p.contract.exp,
        });

        if (errorMsg !== undefined && errorMsg) {
          return errorMsg;
        }
      }
    }

    await contractNegotiation.afterAccept(userTid);
  }
};

const autoProtect = async (tid: number) => {
  const pids = await expansionDraft.autoProtect(tid);
  await expansionDraft.updateProtectedPids(tid, pids);
  await toUI("realtimeUpdate", [["gameAttributes"]]);
};

const cancelExpansionDraft = async () => {
  const expansionDraft = g.get("expansionDraft");
  if (expansionDraft.phase !== "protection") {
    throw new Error("Invalid expansion draft phase");
  }
  for (let i = 0; i < expansionDraft.expansionTids.length; i++) {
    await removeLastTeam();
  }
  await league.setGameAttributes({
    expansionDraft: { phase: "setup" },
    phase: g.get("nextPhase"),
    nextPhase: undefined,
  });
  await updatePhase();
  await updatePlayMenu();
};

const updateProtectedPlayers = async ({
  tid,
  protectedPids,
}: {
  tid: number;
  protectedPids: number[];
}) => {
  await expansionDraft.updateProtectedPids(tid, protectedPids);
  await toUI("realtimeUpdate", [["gameAttributes"]]);
};

const startExpansionDraft = async () => {
  await expansionDraft.start();
  await toUI("realtimeUpdate", [["gameAttributes"]]);
};

const onLiveSimOver = async () => {
  local.liveSimRatingsStatsPopoverPlayers = undefined;

  await toUI("updateLocal", [{ liveGameInProgress: false, liveGameStatus: undefined }]);
};

const updateLiveGameStatus = async (liveGameStatus: LiveGameStatusUI) => {
  await toUI("updateLocal", [{ liveGameStatus }]);
};

const updateBudget = async ({
  budgetLevels,
  adjustForInflation,
  autoTicketPrice,
}: {
  budgetLevels: {
    coaching: number;
    facilities: number;
    health: number;
    scouting: number;
    ticketPrice: number;
  };
  adjustForInflation: boolean;
  autoTicketPrice: boolean;
}) => {
  const userTid = g.get("userTid");

  const t = await idb.cache.teams.get(userTid);
  if (!t) {
    throw new Error("Invalid tid");
  }

  for (const key of helpers.keys(budgetLevels)) {
    // Check for NaN before updating
    // eslint-disable-next-line no-self-compare
    if (budgetLevels[key] === budgetLevels[key]) {
      t.budget[key] = budgetLevels[key];
    }
  }

  if (autoTicketPrice && t.autoTicketPrice === false) {
    t.budget.ticketPrice = await getAutoTicketPriceByTid(userTid);
  }

  t.adjustForInflation = adjustForInflation;
  t.autoTicketPrice = autoTicketPrice;

  await idb.cache.teams.put(t);
  await toUI("realtimeUpdate", [["teamFinances"]]);
};

const updateLeague = async ({ lid, obj }: { lid: number; obj: Partial<League> }) => {
  await league.updateMeta(obj, lid, true);
  await toUI("realtimeUpdate", [["leagues"]]);
};

const updatePlayerWatch = async ({ pid, watch }: { pid: number; watch: number }) => {
  const p = await idb.getCopy.players({ pid }, "noCopyCache");

  if (p) {
    if (watch < 1 || watch > g.get("numWatchColors")) {
      delete p.watch;
    } else {
      p.watch = watch;
    }
    await idb.cache.players.put(p);
    await Promise.all([
      toUI("crossTabEmit", [["updateWatch", getUpdateWatch([p])]]),
      toUI("realtimeUpdate", [["playerMovement", "watchList"]]),
    ]);
  }
};

const getPlayersNextWatch = (players: Player[]) => {
  const watchCounts = new Map<number, number>();
  for (const p of players) {
    const watch = p.watch ?? 0;
    const count = watchCounts.get(watch) ?? 0;
    watchCounts.set(watch, count + 1);
  }
  const mostCommonCurrentWatch = maxBy(Array.from(watchCounts.entries()), 1)![0];

  const nextWatch = (mostCommonCurrentWatch + 1) % (g.get("numWatchColors") + 1);

  if (nextWatch === 0) {
    return undefined;
  }

  return nextWatch;
};

const updatePlayersWatch = async ({ pids, watch }: { pids: number[]; watch?: number }) => {
  // Need to get all players to see what the new watch value should be!
  const players = await idb.getCopies.players({ pids: Array.from(new Set(pids)) }, "noCopyCache");

  if (players.length === 0) {
    return;
  }

  let nextWatch = watch ?? getPlayersNextWatch(players);
  if (nextWatch === 0) {
    // If we're clearing follow markers, watch value is 0, but we want to make it undefined in player object
    nextWatch = undefined;
  }

  for (const p of players) {
    // Only update players who changed
    if (p.watch !== nextWatch) {
      if (nextWatch === undefined) {
        delete p.watch;
      } else {
        p.watch = nextWatch;
      }
      await idb.cache.players.put(p);
    }
  }

  await Promise.all([
    toUI("crossTabEmit", [["updateWatch", getUpdateWatch(players)]]),
    toUI("realtimeUpdate", [["playerMovement", "watchList"]]),
  ]);
};

const updatePlayingTime = async ({ pid, ptModifier }: { pid: number; ptModifier: number }) => {
  const p = await idb.cache.players.get(pid);
  if (!p) {
    throw new Error("Invalid pid");
  }
  p.ptModifier = ptModifier;
  await idb.cache.players.put(p);
  await toUI("realtimeUpdate", [["playerMovement"]]);
};

const clearTrade = async (type: "all" | "other" | "user" | "keepUntradeable") => {
  await trade.clear(type);
  await toUI("realtimeUpdate", []);
};

const proposeTrade = async (forceTrade: boolean, conditions: Conditions) => {
  const { teams } = await trade.get();
  const dv = await team.valueChange(
    teams[1].tid,
    teams[0].pids,
    teams[1].pids,
    teams[0].dpids,
    teams[1].dpids,
    undefined,
    g.get("userTid"),
  );
  const aiWillAcceptTrade = dv > 0;
  if (aiWillAcceptTrade && teams[1].pids.length === 0 && teams[1].dpids.length === 0) {
    let assetsText;
    const numAssets = teams[0].pids.length + teams[0].dpids.length;
    if (teams[0].pids.length === 0) {
      assetsText = helpers.plural("Pick", numAssets);
    } else if (teams[0].dpids.length === 0) {
      assetsText = helpers.plural("Player", numAssets);
    } else {
      assetsText = helpers.plural("Asset", numAssets);
    }

    const proceed = await toUI(
      "confirm",
      [
        "Are you sure you want to propose a trade where you receive nothing?",
        {
          okText: `Give Away ${assetsText}`,
        },
      ],
      conditions,
    );

    if (!proceed) {
      return;
    }
  }

  const output = await trade.propose(forceTrade);
  await toUI("realtimeUpdate", []);
  return output;
};

const confirmTradeOffer = async (arg: Parameters<typeof confirmTradeOfferCore>[0]) => {
  const output = await confirmTradeOfferCore(arg);
  if (output.ok) {
    await toUI("realtimeUpdate", []);
  }
  return output;
};

const toggleColaOptOut = async () => {
  const t = await idb.cache.teams.get(g.get("userTid"));
  if (!t) {
    throw new Error("Should never happen");
  }

  t.colaOptOut = !t.colaOptOut;
  await idb.cache.teams.put(t);

  await toUI("realtimeUpdate", [["draftLottery"]]);
};

const updateTrade = async (teams: TradeTeams) => {
  await trade.updatePlayers(teams);
  await toUI("realtimeUpdate", []);
};

export default {
  actions,
  playMenu,
  main: {
    acceptContractNegotiation,
    beforeView,
    cancelContractNegotiation,
    clearTrade,
    createLeague,
    draftLottery,
    draftUser,
    getAchievementsSheet,
    getBornLoc,
    getDraftPicksSheet,
    getInboxSheet,
    getInjuriesSheet,
    getMessageSheet,
    getLeagueInfo,
    getLeagues,
    getNegotiationProps,
    getTeamHistorySheet,
    getTeamRosterSheet,
    getTradeQuote,
    suggestTradePackage,
    previewTradeOffer,
    evaluateTradeOffer,
    confirmTradeOffer,
    getLocal,
    getPlayerRangeFooterStats,
    getPlayerWatch,
    getProjectedAttendance,
    ping,
    init,
    lockSet,
    proposeTrade,
    ratingsStatsPopoverInfo,
    reSignAll,
    releasePlayer,
    removeLeague,
    reorderRoster,
    retiredJerseyNumberDelete,
    retiredJerseyNumberUpsert,
    runBefore,
    autoProtect,
    cancelExpansionDraft,
    updateProtectedPlayers,
    startExpansionDraft,
    toggleColaOptOut,
    onLiveSimOver,
    updateLiveGameStatus,
    updateBudget,
    updateLeague,
    updatePlayerWatch,
    updatePlayersWatch,
    updatePlayingTime,
    updateTrade,
  },
};
